import { Box } from "@modernatx-internal/component-library";
import { useRouter } from "next/router";
import React from "react";

import { config } from "@/config/environment";
import type { PrefectureFinderBlockProps } from "@/content-V2/types";
import { GoogleAPIProvider } from "@/finder/googleMaps/GoogleAPIProvider";
import { GoogleMapsProvider } from "@/finder/googleMaps/GoogleMapsProvider";
import { FinderContextProvider, useFinder } from "@/finder/hooks/V2/useFinder";
import fetchLocations from "@/finder/utils/fetchLocations";
import { getPrefectureBounds } from "@/finder/utils/prefectureBounds";
import type { FinderLocation } from "@/types/FinderLocation";

import { FinderList } from "./FinderList/FinderList";
import FinderMap from "./FinderMap/FinderMap";
import MobileControls from "./MobileControls";
import { PrefectureSearch } from "./Search/PrefectureSearch";

const JP_SEARCH_INDEX = "branded";

const FinderWithoutContext: React.FC<PrefectureFinderBlockProps> = ({ map, search }) => {
  const router = useRouter();
  const { resultsView, setSearchResultsManually, searchValueSet } = useFinder();
  const prefectureParam = React.useMemo(() => {
    let prefecture = "";
    const rawPrefecture = router.query.prefecture;
    prefecture = typeof rawPrefecture === "string" ? rawPrefecture.trim() : "";

    return Boolean(getPrefectureBounds(prefecture)) ? prefecture : "";
  }, [router.query.prefecture]);

  const prefectureVaccineValues = React.useMemo(() => {
    return search.vaccineDropdownOptions?.map((option) => option.value) ?? [];
  }, [search.vaccineDropdownOptions]);

  const fetchPrefectureResults = React.useCallback(
    async (prefecture: string) => {
      const params = new URLSearchParams({
        stateProvince: prefecture,
        country: "jp",
        type: JP_SEARCH_INDEX
      });

      let locations: FinderLocation[] = [];

      try {
        locations = await fetchLocations(params);
      } catch (_error) {
        locations = [];
      }

      const prefectureBounds = getPrefectureBounds(prefecture);
      const prefectureCenter = prefectureBounds
        ? {
            latitude: (prefectureBounds.north + prefectureBounds.south) / 2,
            longitude: (prefectureBounds.east + prefectureBounds.west) / 2
          }
        : null;

      setSearchResultsManually(locations, {
        place: prefectureCenter
          ? {
              id: prefecture,
              address: prefecture,
              latitude: prefectureCenter.latitude,
              longitude: prefectureCenter.longitude
            }
          : undefined,
        productsSelected: prefectureVaccineValues,
        searchId: params.toString()
      });
      searchValueSet(prefecture);
    },
    [prefectureVaccineValues, setSearchResultsManually, searchValueSet]
  );
  React.useEffect(() => {
    if (!router.isReady) {
      return;
    }

    if (!prefectureParam) {
      const hasInvalidPrefecture = typeof router.query.prefecture === "string";

      // only run router.replace when it has an invalid param. skip when param is already empty.
      if (hasInvalidPrefecture) {
        router.replace("/jp/finder");
      }

      return;
    }

    fetchPrefectureResults(prefectureParam);
  }, [router.isReady, prefectureParam, fetchPrefectureResults, router]);

  return (
    <>
      <MobileControls prefectureSearchProps={{ ...search, prefectureParam }} />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          overflow: [resultsView === "mobileList" ? "hidden" : "auto", null, "hidden"],
          position: "relative"
        }}
      >
        {map && <FinderMap isSearchAreaEnabled={false} />}
        <Box
          id="view-column" // This ID is used for adjusting the map view offset
          sx={{
            display: ["none", null, "flex"],
            flexDirection: "column",
            gap: "spacing-5",
            height: "100%",
            mx: "spacing-8",
            pointerEvents: "auto",
            position: "absolute",
            pt: "spacing-8",
            pb: "spacing-14", // extra padding for the footer
            width: () => "21.875rem",
            zIndex: 2
          }}
        >
          <Box
            sx={{
              borderRadius: "radius-2",
              boxShadow: "shadow-300"
            }}
          >
            <PrefectureSearch {...search} prefectureParam={prefectureParam} />
          </Box>
          <FinderList prefectureParam={prefectureParam} />
        </Box>
        <Box
          sx={{
            display: ["flex", null, "none"],
            flexDirection: resultsView === "mobileList" ? "column" : undefined,
            flexGrow: resultsView === "mobileList" ? 1 : 0,
            minHeight: resultsView === "mobileList" ? 0 : undefined
          }}
        >
          <FinderList prefectureParam={prefectureParam} />
        </Box>
      </Box>
    </>
  );
};

export const PrefectureFinder: React.FC<PrefectureFinderBlockProps> = (props) => {
  const { map, search, mobileNavLocalizedHeaders, vaccineProductConfig, noResultsText } = props;

  return (
    <GoogleAPIProvider apiKey={config.googleMapsApiKey} language={map.language}>
      <GoogleMapsProvider
        defaultCenter={{ lat: map.latitude, lng: map.longitude }}
        defaultZoom={map.zoom}
        {...map}
      >
        <FinderContextProvider
          mobileNavLocalizedHeaders={mobileNavLocalizedHeaders}
          noResultsText={noResultsText}
          searchIndex={JP_SEARCH_INDEX}
          showPharmacyLogos={props.showPharmacyLogos}
          vaccineDropdownOptions={search.vaccineDropdownOptions}
          vaccineProductConfig={vaccineProductConfig}
        >
          <FinderWithoutContext {...props} />
        </FinderContextProvider>
      </GoogleMapsProvider>
    </GoogleAPIProvider>
  );
};
